// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the opponent.
//
// ── IT CANNOT CHEAT, AND THAT IS A TYPE, NOT A PROMISE ───────────────────────────────────────────
//
// Everything below takes a `SeatView`: the public table plus THIS seat's own satchel. There is no
// `crate` field to read and no rival satchel to peek at, so a clairvoyant bot does not compile.
// This module does not import `./secret` at all, and tests/sight.test.ts fails the build if the
// literals `secret`, `.crate[` or `.satchels[` ever appear in this file.
//
// ── WHY AN HONEST BOT IS STILL STRONG ────────────────────────────────────────────────────────────
//
// Almost nothing here is actually hidden. Every card that enters a satchel came off a face-up berth,
// and every sale, barter and dump is announced. So the rival's holding is
//   opening five (unknown) + everything they took (known) - everything they spent (known)
// and the ONLY hidden state in the whole game is those five opening cards plus the crate order. The
// belief below is three integer records updated in O(5) per event, including the retroactive
// constraint: when a rival spends more of a ware than they were ever seen taking, the excess must
// have come from their opening deal, so one unknown slot resolves.
//
// ── THE SEARCH ───────────────────────────────────────────────────────────────────────────────────
//
// Belief-driven perfect-information Monte Carlo: sample the unknowns a few times, run a beamed
// alpha-beta on each fully-determined game, average the root values, take the argmax. Tiers differ
// in MODEL — determinisations, depth, beam, how much of the belief they use, how noisy their pick is
// — and never in ACCESS. All three read the identical `SeatView`.
//
// The price oracle is a seam on purpose. `SIGHTED` reads the live tariffs; `BLIND` answers every
// single lookup with the ware's INITIAL top chit and reports every column as full. That is the
// negative-gap control from tests/gap.test.ts, and it is a seam rather than a setup flag precisely
// because a flag can be quietly restored by a state copy — this one is consulted on every lookup or
// not at all.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  MIN_SALE,
  PORTER,
  TARIFF,
  WARES,
  bountyTierFor,
  buildDeck,
  isWare,
  type Card,
  type Ware,
} from './wares';
import {
  clonePublic,
  cloneFull,
  legalActions,
  other,
  quote,
  scoreOf,
  step,
  sum,
  type Action,
  type FullState,
  type PublicState,
  type Seat,
  type SeatView,
} from './rules';
import { HIRE_BONUS } from './wares';

export type TierId = 'hawker' | 'mercer' | 'chandler';

export interface Skill {
  readonly id: TierId;
  readonly name: string;
  readonly blurb: string;
  /** How many times the hidden state is sampled. */
  readonly dets: number;
  readonly depth: number;
  /** Branching kept at each ply, outermost first. */
  readonly beam: readonly number[];
  /** `full` uses the retroactive constraint; `pool` forgets which cards the rival took. */
  readonly belief: 'pool' | 'full';
  /** Softmax temperature on the root values. 0 = always the best move. */
  readonly tau: number;
  /** Hard node cap per determinisation — reproducible across machines, unlike a wall clock. */
  readonly nodes: number;
}

export const TIERS: Record<TierId, Skill> = {
  hawker: {
    id: 'hawker',
    name: 'Hawker',
    blurb: 'Sells the moment a set looks worth anything. Rarely reads the column.',
    dets: 1,
    depth: 1,
    beam: [8],
    belief: 'pool',
    tau: 1.1,
    nodes: 400,
  },
  mercer: {
    id: 'mercer',
    name: 'Mercer',
    blurb: 'Counts the column and holds for a bounty — but forgets exactly what you took.',
    dets: 3,
    depth: 3,
    beam: [8, 4, 3],
    belief: 'pool',
    tau: 0.35,
    nodes: 3500,
  },
  chandler: {
    id: 'chandler',
    name: 'Chandler',
    blurb: 'Tracks every card you have touched and will empty a tariff under you on purpose.',
    dets: 6,
    depth: 4,
    beam: [10, 5, 4, 3],
    belief: 'full',
    tau: 0,
    nodes: 9000,
  },
};

export const TIER_IDS: readonly TierId[] = ['hawker', 'mercer', 'chandler'];
export const DEFAULT_TIER: TierId = 'mercer';

export function tierOf(id: string | null | undefined): Skill {
  if (typeof id === 'string' && Object.hasOwn(TIERS, id)) return TIERS[id as TierId];
  return TIERS[DEFAULT_TIER];
}

// ── the price oracle ────────────────────────────────────────────────────────────────────────────

/**
 * Every question the evaluation asks about PRICE goes through here. Nothing else in the bot reads
 * `pub.tariffs` — which is what makes the blind arm a genuine control instead of a mutilated bot.
 */
export interface Oracle {
  readonly id: string;
  /** What a sale of n of `w` would pay, chit by chit. */
  quote(p: PublicState, w: Ware, n: number): number[];
  /** Chits still on the column. */
  left(p: PublicState, w: Ware): number;
  /** How much of the column's original depth remains, 0..1. */
  fill(p: PublicState, w: Ware): number;
}

export const SIGHTED: Oracle = {
  id: 'sighted',
  quote: (p, w, n) => quote(p, w, n),
  left: (p, w) => p.tariffs[w].length,
  fill: (p, w) => p.tariffs[w].length / TARIFF[w].length,
};

/**
 * THE NEGATIVE-GAP CONTROL. Identical search, identical depth, identical beam, identical belief,
 * identical tuning, identical transition function. One thing changes: it believes every ware still
 * pays its opening price and that no column is anywhere near dry.
 *
 * Stated honestly: within its own search horizon it still observes what a sale actually banks, so
 * it retains partial sight of the decline. The measured gap is therefore a LOWER bound on how
 * load-bearing the depleting price is, not an upper one.
 */
export const BLIND: Oracle = {
  id: 'tariffblind',
  quote: (_p, w, n) => TARIFF[w].slice(0, Math.max(0, n)),
  left: (_p, w) => TARIFF[w].length,
  fill: () => 1,
};

// ── tuning ──────────────────────────────────────────────────────────────────────────────────────

export interface Weights {
  /** Value of a held set, as a fraction of its quoted price, scaled by how full the column is. */
  readonly holdFloor: number;
  readonly holdFill: number;
  /**
   * How much of that discount to give back as SUPPLY runs out — and this one was found by the sim,
   * not reasoned out. Discounting a holding because its column is draining is right when there are
   * still cards coming; when there are not, the card in your hand is the last one you will get and
   * the discount is simply wrong. Without this term the bot LOST to both of its own control arms in
   * Dearth (30% and 46%), which is the signature of a mis-tuned evaluation rather than of a mode
   * where the mechanic does nothing.
   */
  readonly scarcity: number;
  /** A holding below its minimum sale size is nearly worthless until it is completed. */
  readonly shortSet: number;
  /** How much of a reachable bounty to count. */
  readonly bountyEquity: number;
  /** Porters: half the hire bonus at a four-porter lead, plus a little for barter liquidity. */
  readonly hire: number;
  readonly liquidity: number;
  /** Cards over this many in a six-card satchel start costing tempo. */
  readonly satchelSoft: number;
  readonly satchelPressure: number;
  /** Flat bonus for actually being ahead at a terminal node, beyond the margin itself. */
  readonly winStep: number;
}

/**
 * TWO OF THESE ARE MEASURED AT ZERO, AND THAT IS THE FINDING, NOT AN OVERSIGHT.
 *
 * The design called for a holding to be discounted as its column drained (`holdFill`), on the
 * obvious reasoning that a nearly-dry ware may never be realised — and then for a supply-conditioned
 * term (`scarcity`) to rescue that when the first one misbehaved. With both live, the bot LOST to
 * its own control arms in Dearth: 22% against an arm with the discount removed, and 46% against the
 * price-blind arm. A bot that loses to a deliberately misinformed copy of itself is not measuring a
 * mode, it is mis-tuned. Removing both improved the price gap in every single mode (70→77, 92→96,
 * 46→58).
 *
 * Re-measured against the shipped bot, each restored weight now lands inside noise, so the honest
 * statement is NOT RESOLVABLE rather than "harmful" — and the reason to ship zero is that a weight
 * which cannot be shown to help does not belong in an evaluation. The likely cause is the same one
 * that retired a weight in two sibling games: the SEARCH already plays through the consequence of a
 * column running out, so a static discount on top of it is double-counting. Both arms are kept in
 * gap.test.ts so that re-deriving the same plausible idea turns something red.
 */
export const TUNING: Weights = {
  holdFloor: 1,
  holdFill: 0,
  scarcity: 0,
  shortSet: 0.45,
  bountyEquity: 0.6,
  hire: 0.5,
  liquidity: 0.3,
  satchelSoft: 4,
  satchelPressure: 0.35,
  winStep: 3,
};

// ── belief ──────────────────────────────────────────────────────────────────────────────────────

export interface Belief {
  /** Every card neither of us can account for: the rival's opening remnant plus the crate. */
  readonly unseen: Card[];
  /** Rival cards we watched them take and have not watched them spend. */
  readonly rivalKnown: Ware[];
  /** How many of the rival's cards are still their opening five. */
  readonly rivalUnknown: number;
}

/**
 * Rebuild the belief from the public log and this seat's own satchel. Pure, and deliberately
 * recomputed from scratch each turn — an incrementally-maintained belief that drifts is a bug you
 * find three weeks later in a game nobody can reproduce.
 */
export function beliefFrom(view: SeatView): Belief {
  const p = view.pub;
  const rival = other(view.seat);
  const pool = new Map<Card, number>();
  for (const c of buildDeck()) pool.set(c, (pool.get(c) ?? 0) + 1);
  const drop = (c: Card): void => {
    pool.set(c, Math.max(0, (pool.get(c) ?? 0) - 1));
  };

  // Everything whose location is accounted for. A TAKE is deliberately NOT dropped here: it only
  // moves a card from a berth into a satchel, and both of those are counted below on their own.
  for (const c of p.trestle) if (c !== null) drop(c);
  if (p.crateTop !== null) drop(p.crateTop);
  for (const w of view.satchel) drop(w);
  for (let i = 0; i < p.gang[0] + p.gang[1]; i++) drop(PORTER);

  const known = new Map<Ware, number>();
  let unknown = 5;
  const spend = (w: Ware): void => {
    const n = known.get(w) ?? 0;
    if (n > 0) known.set(w, n - 1);
    // The retroactive constraint: they spent a ware we never watched them take, so one of their
    // five opening cards is now accounted for — and gone.
    else unknown--;
  };

  for (const ev of p.log) {
    switch (ev.k) {
      case 'take':
        if (ev.seat === rival) known.set(ev.ware, (known.get(ev.ware) ?? 0) + 1);
        break;
      case 'barter':
        for (const w of ev.took) {
          if (ev.seat === rival) known.set(w, (known.get(w) ?? 0) + 1);
        }
        for (const w of ev.gaveWares) {
          drop(w); // into the bale, out of the game
          if (ev.seat === rival) spend(w);
        }
        for (let i = 0; i < ev.gavePorters; i++) drop(PORTER);
        break;
      case 'sell':
        for (let i = 0; i < ev.cards; i++) {
          drop(ev.ware);
          if (ev.seat === rival) spend(ev.ware);
        }
        break;
      case 'dump':
        drop(ev.card);
        if (ev.seat === rival && isWare(ev.card)) spend(ev.card);
        break;
      default:
        break;
    }
  }

  const rivalKnown: Ware[] = [];
  for (const [w, n] of known) for (let i = 0; i < n; i++) rivalKnown.push(w);
  for (const w of rivalKnown) drop(w);

  const unseen: Card[] = [];
  for (const [c, n] of pool) for (let i = 0; i < n; i++) unseen.push(c);

  // The reconstruction must agree with the authoritative count. If it does not — a spectator that
  // connected mid-deal holds a partial log — fall back to knowing nothing about their hand rather
  // than sampling an illegal world. A wrong belief must degrade, never throw.
  const size = p.satchelCount[rival];
  if (unknown < 0 || rivalKnown.length + unknown !== size) {
    for (const w of rivalKnown) unseen.push(w);
    return { unseen, rivalKnown: [], rivalUnknown: size };
  }
  return { unseen, rivalKnown, rivalUnknown: unknown };
}

/**
 * Sample one complete, legal world consistent with everything we know. The face-up crate top is
 * pinned where it belongs; the rival's unknown slots are drawn from the unseen WARES (a porter is
 * never in a satchel); everything left is the crate, in a random order.
 */
export function determinise(view: SeatView, belief: Belief, rng: Rng, mode: 'pool' | 'full'): FullState {
  const p = view.pub;
  const rival = other(view.seat);
  const bag = [...belief.unseen];
  let known = belief.rivalKnown;
  let need = belief.rivalUnknown;
  if (mode === 'pool') {
    // Deliberately forget which cards we watched them take: a strictly weaker model, but the
    // accounting stays exact, so the sampled world is still perfectly legal.
    for (const w of known) bag.push(w);
    need = p.satchelCount[rival];
    known = [];
  }
  // Fisher-Yates from the seeded rng, so a determinisation is reproducible.
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  const rivalSatchel: Ware[] = [...known];
  const crate: Card[] = [];
  if (p.crateTop !== null) crate.push(p.crateTop);
  for (const c of bag) {
    if (rivalSatchel.length < known.length + need && isWare(c)) rivalSatchel.push(c);
    else crate.push(c);
  }
  const satchels: [Ware[], Ware[]] = [[], []];
  satchels[view.seat] = [...view.satchel];
  satchels[rival] = rivalSatchel;
  return { pub: clonePublic(p), sec: { satchels, crate } };
}

// ── evaluation ──────────────────────────────────────────────────────────────────────────────────

/**
 * How little is still coming, 0..1. PUBLIC information — the crate count and the trestle are on
 * screen — so it is not part of the price oracle and both control arms see it identically.
 */
export function supplyPressure(p: PublicState): number {
  let wares = 0;
  for (const c of p.trestle) if (c !== null && c !== 'porter') wares++;
  return Math.max(0, Math.min(1, 1 - (p.crateCount + wares) / 20));
}

/** What a holding of `n` of ware `w` is worth to a player who has not sold it yet. */
export function holdValue(p: PublicState, w: Ware, n: number, o: Oracle, t: Weights): number {
  if (n <= 0) return 0;
  const face = sum(o.quote(p, w, n));
  const rho = Math.max(
    0.3,
    Math.min(1, t.holdFloor + t.holdFill * o.fill(p, w) + t.scarcity * supplyPressure(p)),
  );
  const short = n < MIN_SALE(w) ? t.shortSet : 1;
  return face * rho * short;
}

/** The best bounty this satchel could claim in a single sale right now. */
export function bountyReach(p: PublicState, satchel: readonly Ware[], o: Oracle): number {
  let best = 0;
  for (const w of WARES) {
    let n = 0;
    for (const c of satchel) if (c === w) n++;
    if (n < 3) continue;
    const paid = sum(o.quote(p, w, n));
    const tier = bountyTierFor(paid, n);
    if (tier === null) continue;
    const stack = p.bounties[tier];
    if (stack.length > 0 && stack[0] > best) best = stack[0];
  }
  return best;
}

export function sideValue(
  p: PublicState,
  satchel: readonly Ware[],
  seat: Seat,
  o: Oracle,
  t: Weights,
): number {
  const s = scoreOf(p, seat);
  let v = s.chits + s.bounty + s.tare;
  const counts = new Map<Ware, number>();
  for (const w of satchel) counts.set(w, (counts.get(w) ?? 0) + 1);
  for (const [w, n] of counts) v += holdValue(p, w, n, o, t);
  v += t.bountyEquity * bountyReach(p, satchel, o);
  const diff = p.gang[seat] - p.gang[other(seat)];
  v += HIRE_BONUS * t.hire * Math.max(-1, Math.min(1, diff / 4));
  v += t.liquidity * p.gang[seat];
  v -= t.satchelPressure * Math.max(0, satchel.length - t.satchelSoft);
  return v;
}

export function evaluate(fs: FullState, root: Seat, o: Oracle, t: Weights): number {
  const p = fs.pub;
  if (p.over) {
    const d = scoreOf(p, root).total - scoreOf(p, other(root)).total;
    return d + (d > 0 ? t.winStep : d < 0 ? -t.winStep : 0);
  }
  return (
    sideValue(p, fs.sec.satchels[root], root, o, t) -
    sideValue(p, fs.sec.satchels[other(root)], other(root), o, t)
  );
}

// ── search ──────────────────────────────────────────────────────────────────────────────────────

interface Ctx {
  nodes: number;
  cap: number;
  o: Oracle;
  t: Weights;
  root: Seat;
  beam: readonly number[];
}

function applied(fs: FullState, a: Action): FullState | null {
  const next = cloneFull(fs);
  return step(next, a) ? next : null;
}

function ordered(fs: FullState, ctx: Ctx, width: number): { a: Action; fs: FullState; v: number }[] {
  const seat = fs.pub.toMove;
  const out: { a: Action; fs: FullState; v: number }[] = [];
  for (const a of legalActions(fs.pub, seat, fs.sec.satchels[seat])) {
    const next = applied(fs, a);
    if (!next) continue;
    ctx.nodes++;
    out.push({ a, fs: next, v: evaluate(next, ctx.root, ctx.o, ctx.t) });
  }
  out.sort((x, y) => (seat === ctx.root ? y.v - x.v : x.v - y.v));
  return out.slice(0, Math.max(1, width));
}

function ab(fs: FullState, depth: number, alpha: number, beta: number, ctx: Ctx): number {
  if (fs.pub.over) return evaluate(fs, ctx.root, ctx.o, ctx.t);
  if (depth <= 0 || ctx.nodes >= ctx.cap) return evaluate(fs, ctx.root, ctx.o, ctx.t);
  const width = ctx.beam[Math.min(ctx.beam.length - 1, ctx.beam.length - depth)] ?? 3;
  const kids = ordered(fs, ctx, width);
  if (kids.length === 0) return evaluate(fs, ctx.root, ctx.o, ctx.t);
  const maxing = fs.pub.toMove === ctx.root;
  let best = maxing ? -Infinity : Infinity;
  for (const k of kids) {
    const v = ab(k.fs, depth - 1, alpha, beta, ctx);
    if (maxing) {
      if (v > best) best = v;
      if (best > alpha) alpha = best;
    } else {
      if (v < best) best = v;
      if (best < beta) beta = best;
    }
    if (alpha >= beta) break;
  }
  return best;
}

export interface Choice {
  readonly action: Action;
  readonly nodes: number;
  /** Root value of every candidate, for the "how close was it" agency statistic. */
  readonly values: number[];
}

/**
 * Pick a move. The only entry point the game and the sim ever call.
 *
 * A note on the rng: it is threaded through determinisation AND the softmax, so a given
 * (seed, ply, tier) always produces the same move. That is what makes an audited event stream
 * replayable and a balance number reproducible.
 */
export function chooseAction(view: SeatView, skill: Skill, rng: Rng, o: Oracle = SIGHTED, t: Weights = TUNING): Choice {
  const p = view.pub;
  const seat = view.seat;
  const roots = legalActions(p, seat, view.satchel);
  if (roots.length === 0) return { action: { k: 'dump' }, nodes: 0, values: [] };
  if (roots.length === 1) return { action: roots[0], nodes: 0, values: [0] };

  const belief = beliefFrom(view);
  const totals = new Array(roots.length).fill(0) as number[];
  let nodes = 0;

  for (let d = 0; d < skill.dets; d++) {
    const world = determinise(view, belief, rng, skill.belief);
    const ctx: Ctx = {
      nodes: 0,
      cap: skill.nodes,
      o,
      t,
      root: seat,
      beam: skill.beam,
    };
    for (let i = 0; i < roots.length; i++) {
      const next = applied(world, roots[i]);
      if (!next) {
        totals[i] += -Infinity;
        continue;
      }
      ctx.nodes++;
      totals[i] += ab(next, skill.depth - 1, -Infinity, Infinity, ctx);
    }
    nodes += ctx.nodes;
  }

  const values = totals.map((v) => v / skill.dets);
  let pick = 0;
  if (skill.tau <= 0) {
    for (let i = 1; i < values.length; i++) if (values[i] > values[pick]) pick = i;
  } else {
    const top = Math.max(...values);
    const w = values.map((v) => Math.exp((v - top) / skill.tau));
    const total = w.reduce((a, b) => a + b, 0);
    let r = rng() * total;
    pick = w.length - 1;
    for (let i = 0; i < w.length; i++) {
      r -= w[i];
      if (r <= 0) {
        pick = i;
        break;
      }
    }
  }
  return { action: roots[pick], nodes, values };
}

/** A per-turn rng that is a pure function of the deal seed, so a replay picks the same moves. */
export function turnRng(seed: number, ply: number, tag: string): Rng {
  return makeRng(`${seed}:${ply}:${tag}`);
}
